/**
 * RiskScoring — Four score cards + confidence + reason display.
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MetricCard } from '../../../components/MetricCard';
import { LoadingState } from '../../../components/LoadingState';
import { ErrorState } from '../../../components/ErrorState';
import { useAuth } from '../../../components/AuthProvider';
import { listDocuments } from '../../../api/documents';
import apiClient from '../../../api/client';
import type { RiskScores } from '../../../api/types';

function getRiskColor(score: number): string {
  if (score >= 8) return 'text-red-700';
  if (score >= 6) return 'text-amber-700';
  if (score >= 4) return 'text-yellow-700';
  return 'text-green-700';
}

function getRiskLabel(score: number): string {
  if (score >= 8) return 'Critical';
  if (score >= 6) return 'High';
  if (score >= 4) return 'Medium';
  return 'Low';
}

export function RiskScoring() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get('doc') || '';
  const [scores, setScores] = useState<RiskScores | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      // If a specific document is provided, fetch its risk scores
      // Otherwise try to get the first completed document
      let docId = documentId;
      if (!docId) {
        const docsResponse = await listDocuments(user.tenantId);
        const completedDocs = docsResponse.documents.filter(
          (d) => d.status === 'completed' || d.status === 'scored' || d.status === 'indexed'
        );
        const completedDoc = completedDocs.length > 0 ? completedDocs[completedDocs.length - 1] : null;
        if (completedDoc) {
          docId = completedDoc.document_id;
        }
      }

      if (!docId) {
        setScores(null);
        setLoading(false);
        return;
      }

      const response = await apiClient.get(`/cti/workflow/${docId}`, {
        params: { tenant_id: user.tenantId, stage: 'risk' },
      });
      setScores(response.data.scores || null);
    } catch {
      setError('Failed to load risk scores.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, documentId]);

  if (loading) return <LoadingState message="Loading risk scores..." />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;

  if (!scores) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-heading-lg font-bold text-slate-text">Risk Scoring</h1>
          <p className="text-body text-slate-muted mt-1">
            Automated risk assessment for analyzed threats
          </p>
        </div>
        <div className="glass-card text-center py-12">
          <p className="text-body text-slate-muted">
            No risk scores yet. Upload and process a CTI document to generate risk assessments.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-heading-lg font-bold text-slate-text">Risk Scoring</h1>
        <p className="text-body text-slate-muted mt-1">
          Automated risk assessment for analyzed threats
        </p>
      </div>

      {/* Overall risk */}
      <div className="glass-card !border-3 text-center">
        <p className="text-[16px] font-semibold text-slate-muted mb-2">Overall Risk Score</p>
        <p className={`text-[48px] font-bold ${getRiskColor(scores.overall_risk)}`}>
          {scores.overall_risk.toFixed(1)}/10
        </p>
        <p className={`text-section font-bold ${getRiskColor(scores.overall_risk)}`}>
          {getRiskLabel(scores.overall_risk)}
        </p>
      </div>

      {/* Score cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Severity"
          value={`${scores.severity_score.toFixed(1)}/10`}
          subtitle={getRiskLabel(scores.severity_score)}
        />
        <MetricCard
          title="Exploitability"
          value={`${scores.exploitability_score.toFixed(1)}/10`}
          subtitle={getRiskLabel(scores.exploitability_score)}
        />
        <MetricCard
          title="Impact"
          value={`${scores.impact_score.toFixed(1)}/10`}
          subtitle={getRiskLabel(scores.impact_score)}
        />
        <MetricCard
          title="Confidence"
          value={`${(scores.confidence * 100).toFixed(0)}%`}
          subtitle="Model certainty"
        />
      </div>

      {/* Reason */}
      <div className="glass-card">
        <h3 className="text-section-sm font-bold text-slate-text mb-3">Assessment Rationale</h3>
        <p className="text-body text-slate-text leading-relaxed">{scores.reason}</p>
      </div>
    </div>
  );
}
